import Decimal from 'app/src/data/base/Decimal';
import Sale from 'app/src/data/models/Sale';
import SalePayment from 'app/src/data/models/SalePayment';
import InvoiceService from 'root/apps/QuoteMachineIntegration/services/InvoiceService';
import { DepositPaymentOptions } from 'root/apps/QuoteMachineIntegration/state/DepositPaymentOptions';
import Employee from 'root/apps/QuoteMachineIntegration/state/Employee';
import Invoice from 'root/apps/QuoteMachineIntegration/state/Invoice';
import { InvoicePaymentOptions } from 'root/apps/QuoteMachineIntegration/state/InvoicePaymentOptions';
import {
  AddDepositPayload,
  DepositPaymentsPayload,
  InvoicePaymentsPayload,
  InvoicingPayload,
  InvoicingSalePayment,
  SaleDataPayload,
  SalePaymentsPayload,
} from 'root/apps/QuoteMachineIntegration/state/InvoicingPayload';
import Location from 'root/apps/QuoteMachineIntegration/state/Location';

export async function getInvoicingPayload(
  sale: Sale,
  invoicePaymentOptions: InvoicePaymentOptions,
  depositPaymentOptions: DepositPaymentOptions,
  salePayments: InvoicingSalePayment[],
  depositToAdd: Decimal,
  change: Decimal,
  giftCardPayments: SalePayment[]
): Promise<InvoicingPayload> {
  return {
    ...getAddDepositPayload(depositToAdd),
    ...getInvoicePaymentsPayload(invoicePaymentOptions),
    ...getDepositPaymentsPayload(depositPaymentOptions),
    ...getSalePaymentsPayload(salePayments, change),
    ...(await getSaleDataPayload(
      sale,
      salePayments,
      depositToAdd,
      invoicePaymentOptions,
      depositPaymentOptions,
      giftCardPayments
    )),
  };
}

export function getAddDepositPayload(amount: Decimal): AddDepositPayload {
  return {
    depositAmountToAdd: amount.toString(),
  };
}

export function getInvoicePaymentsPayload(
  invoicePaymentOptions: InvoicePaymentOptions
): InvoicePaymentsPayload {
  return {
    ...invoicePaymentOptions,
    invoicesToPay: invoicePaymentOptions?.invoicesToPay ?? [],
  };
}

export function getDepositPaymentsPayload(
  depositPaymentOptions: DepositPaymentOptions
): DepositPaymentsPayload {
  return {
    ...depositPaymentOptions,
    depositPayments: depositPaymentOptions?.depositPayments ?? [],
  };
}

export function getSalePaymentsPayload(
  salePayments: InvoicingSalePayment[],
  change: Decimal,
  cashRoundingDelta: Decimal = new Decimal(0)
): SalePaymentsPayload {
  return {
    payments: salePayments.map((payment) =>
      mapPaymentToInvoicingSalePayment(payment, change, cashRoundingDelta)
    ),
  };
}

export function mapPaymentToInvoicingSalePayment(
  payment: InvoicingSalePayment,
  change: Decimal,
  cashRoundingDelta: Decimal = new Decimal(0)
): InvoicingSalePayment {
  if (payment.paymentTypeId === '1') {
    return {
      ...payment,
      amount: new Decimal(payment.amount).minus(change).toString(),
      cashRoundingDelta: cashRoundingDelta.toString(),
    };
  }
  return {
    ...payment,
    amount: payment.amount.toString(),
  };
}

export async function getSaleDataPayload(
  sale: Sale,
  salePayments: InvoicingSalePayment[],
  depositToAdd: Decimal,
  invoicePaymentOptions: InvoicePaymentOptions,
  depositPaymentOptions: DepositPaymentOptions,
  giftCardPayments: SalePayment[]
): Promise<SaleDataPayload> {
  const employee = new Employee(window.merchantos.session.getEmployee());
  const location = new Location(await InvoiceService.getLocationData());
  const saleData = new Invoice(
    sale.toJSON(),
    employee,
    location,
    `${window.merchantos.account.id}`,
    salePayments,
    invoicePaymentOptions?.invoicesToPay ?? [],
    depositPaymentOptions?.depositPayments ?? [],
    giftCardPayments,
    depositToAdd
  );

  return { saleData };
}
